import * as React from 'react';
import { useRef, useState } from 'react';
import { usePayment } from './components/PaymentContext';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CssBaseline from '@mui/material/CssBaseline';
import Grid from '@mui/material/Grid2';
import Stack from '@mui/material/Stack';
import Step from '@mui/material/Step';
import StepLabel from '@mui/material/StepLabel';
import Stepper from '@mui/material/Stepper';
import Typography from '@mui/material/Typography';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import ChevronLeftRoundedIcon from '@mui/icons-material/ChevronLeftRounded';
import ChevronRightRoundedIcon from '@mui/icons-material/ChevronRightRounded';
import AddressForm from './components/AddressForm';
import getCheckoutTheme from './theme/getCheckoutTheme';
import Info from './components/Info';
import InfoMobile from './components/InfoMobile';
import PaymentForm from './components/PaymentForm';
import Review from './components/Review';
import TemplateFrame from './TemplateFrame';
import Snackbar from '@mui/material/Snackbar'; // Import Snackbar for the pop-up
import axios from 'axios';

import SyncFunds from './SyncFunds.png';
import { PaymentProvider } from './components/PaymentContext';


const steps = ['Payment details', 'Review your order', 'Payment confirmation'];
function getStepContent(step) {
  switch (step) {
    case 0:
      return  <PaymentForm />;
    case 1:
      return <Review />;  
    default:
      throw new Error('Unknown step');
  }
}

const Overlay = ({ open }) => (
  <Box
    sx={{
      display: open ? 'block' : 'none',
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100%',
      height: '100%',
      backgroundColor: 'rgba(0, 0, 0, 0.5)', // Semi-transparent background
      zIndex: 1000, // Ensure it is above other content
    }}
  />
);

export default function Checkout() {
  const [mode, setMode] = React.useState('light');
  const [showCustomTheme, setShowCustomTheme] = React.useState(true);
  const checkoutTheme = createTheme(getCheckoutTheme(mode));
  const defaultTheme = createTheme({ palette: { mode } });
  const [activeStep, setActiveStep] = React.useState(0);
  const [openSnackbar, setOpenSnackbar] = React.useState(false);
  const [isSnackbarActive, setIsSnackbarActive] = useState(false);
  const { email, setEmail } = usePayment();
  const { cardName, setCardName } = usePayment();

  const getData = (data) => {
    setEmail(data.email);
    setCardName(data.firstName);
   }

  // This code only runs on the client side, to determine the system color preference
  React.useEffect(() => {
    // Check if there is a preferred mode in localStorage
    const savedMode = localStorage.getItem('themeMode');
    if (savedMode) {
      setMode(savedMode);
    } else {
      // If no preference is found, it uses system preference
      const systemPrefersDark = window.matchMedia(
        '(prefers-color-scheme: dark)',
      ).matches;
      setMode(systemPrefersDark ? 'dark' : 'light');
    }
  }, []);

  const toggleColorMode = () => {
    const newMode = mode === 'dark' ? 'light' : 'dark';
    setMode(newMode);
    localStorage.setItem('themeMode', newMode); // Save the selected mode to localStorage
  };
  const toggleCustomTheme = () => {
    setShowCustomTheme((prev) => !prev);
  };

  const paymentFormRef = useRef(null);

  const handleNext = () => {
    if (activeStep === 0 && paymentFormRef.current) {
      console.log('Payment Form Ref:', paymentFormRef.current);
      const isValid = paymentFormRef.current.validate();
      if (!isValid) {
        return; // Prevent moving to the next step if validation fails
      }
    }
    if (activeStep === 1) {
      setOpenSnackbar(true);
      setIsSnackbarActive(true); // Set Snackbar active
      
      // Set a timeout to move to the next step after 8 seconds
      setTimeout(async () => {
        try {
          await handlePaymentSuccess(); // Call the payment success function
          
          console.log('Redirecting to the next page...'); // Replace with your navigation logic
          setActiveStep(activeStep + 1); // Move to the next step after the delay
        } catch (error) {
          console.error('Error during payment process:', error);
        } finally {
          setOpenSnackbar(false); // Close the Snackbar
          setIsSnackbarActive(false); // Reset Snackbar active state
        }
      }, 7000); // Wait for 7 seconds
  
      return; // Exit the function to avoid advancing the step immediately
    }
  
    // For all other steps, just move to the next step
    setActiveStep(activeStep + 1);
  };

  const handleBack = () => {
    setActiveStep(activeStep - 1);
  };
  
  const handlePaymentSuccess = async () => {
    
    console.log('Email from context:', email);
    try {
      await axios.post('https://buy.sync-funds.com/fZebJ892Pesv6Ag491/api/send_confirmation.php', { email: email, name: cardName }); // Adjust the path as needed
      console.log('Confirmation email sent successfully');
    } catch (error) {
      console.error('Error sending confirmation email:', error);
    }
  };
  
  return (
    <TemplateFrame
      toggleCustomTheme={toggleCustomTheme}
      showCustomTheme={showCustomTheme}
      mode={mode}
      toggleColorMode={toggleColorMode}
    >
      <PaymentProvider>
      <ThemeProvider theme={showCustomTheme ? checkoutTheme : defaultTheme}>
        <CssBaseline enableColorScheme />
        <Grid container sx={{ height: { xs: '100%', sm: '100dvh' } }}>
          <Grid
            size={{ xs: 12, sm: 5, lg: 4 }}
            sx={{
              display: { xs: 'none', md: 'flex' },
              flexDirection: 'column',
              backgroundColor: 'background.paper',
              borderRight: { sm: 'none', md: '1px solid' },
              borderColor: { sm: 'none', md: 'divider' },
              alignItems: 'start',
              pt: 16,
              px: 10,
              gap: 4,
            }}
          >
            <img src={SyncFunds} alt="Description of Image" style={{ width: 'auto', height: 'auto' }} />
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                width: '100%',
                maxWidth: 500,
              }}
            >
              <Info totalPrice={activeStep >= 2 ? 'A$150.00' : 'A$150.00'} />
            </Box>
          </Grid>
          <Grid
            size={{ sm: 12, md: 7, lg: 8 }}
            sx={{
              display: 'flex',
              flexDirection: 'column',
              maxWidth: '100%',
              width: '100%',
              backgroundColor: { xs: 'transparent', sm: 'background.default' },
              alignItems: 'start',
              pt: { xs: 6, sm: 16 },
              px: { xs: 2, sm: 10 },
              gap: { xs: 4, md: 8 },
            }}
          >
            <Box
              sx={{
                display: 'flex',
                justifyContent: { sm: 'space-between', md: 'flex-end' },
                alignItems: 'center',
                width: '100%',
                maxWidth: { sm: '100%', md: 600 },
              }}
            >
              <Box
                sx={{
                  display: { xs: 'none', md: 'flex' },
                  flexDirection: 'column',
                  justifyContent: 'space-between',
                  alignItems: 'flex-end',
                  flexGrow: 1,
                }}
              >
                <Stepper
                  id="desktop-stepper"
                  activeStep={activeStep}
                  sx={{ width: '100%', height: 40 }}
                >
                  {steps.map((label) => (
                    <Step
                      sx={{ fontFamily: 'math' , ':first-child': { pl: 0 }, ':last-child': { pr: 0 } }}
                      key={label}
                    >
                      <StepLabel sx= {{ fontFamily: 'math' }}>{label}</StepLabel>
                    </Step>
                  ))}
                </Stepper>
              </Box>
            </Box>
            <Card sx={{ display: { xs: 'flex', md: 'none' }, width: '100%', fontFamily: 'math' }}>
              <CardContent
                sx={{
                  display: 'flex',
                  width: '100%',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                }}
              >
                <div>
                  <Typography variant="subtitle2" gutterBottom>
                    Selected products
                  </Typography>
                  <Typography variant="body1">
                    {activeStep >= 2 ? 'A$150.00' : 'A$150.00'}
                  </Typography>
                </div>
                <InfoMobile totalPrice={activeStep >= 2 ? 'A$150.00' : 'A$150.00'} />
              </CardContent>
            </Card>
            <Box
              sx={{
                display: 'flex',
                flexDirection: 'column',
                flexGrow: 1,
                width: '100%',
                maxWidth: { sm: '100%', md: 600 },
                maxHeight: '720px',
                gap: { xs: 5, md: 'none' },
              }}
            >
              <Stepper
                id="mobile-stepper"
                activeStep={activeStep}
                alternativeLabel
                sx={{ display: { sm: 'flex', md: 'none' } }}
              >
                {steps.map((label) => (
                  <Step
                    sx={{
                      ':first-child': { pl: 0 },
                      ':last-child': { pr: 0 },
                      '& .MuiStepConnector-root': { top: { xs: 6, sm: 12 } },
                    }}
                    key={label}
                  >
                    <StepLabel
                      sx={{ '.MuiStepLabel-labelContainer': { maxWidth: '70px' } }}
                    >
                      {label}
                    </StepLabel>
                  </Step>
                ))}
              </Stepper>
              {activeStep === 2 ? (
                <Stack spacing={2} useFlexGap>
                  <Typography variant="h1">✅</Typography>
                  <Typography variant="h5">Payment successfully!</Typography>
                  <Typography variant="body1" sx={{ color: 'text.secondary' }}>
                    We have emailed your payment
                    confirmation.
                  </Typography>
                  {/* <Button
                    variant="contained"
                    sx={{ alignSelf: 'start', width: { xs: '100%', sm: 'auto' } }}
                  >
                    Go to my orders
                  </Button> */}
                </Stack>
              ) : (
                <>
                {activeStep === 0 && <PaymentForm ref={paymentFormRef} onSubmit={getData}/>}
                {activeStep === 1 && <Review />}
                <Box
                  sx={[
                    {
                      display: 'flex',
                      flexDirection: { xs: 'column-reverse', sm: 'row' },
                      alignItems: 'end',
                      flexGrow: 1,
                      gap: 1,
                      pb: { xs: 12, sm: 0 },
                      mt: { xs: 2, sm: 0 },
                      mb: '60px',
                    },
                    activeStep !== 0
                      ? { justifyContent: 'space-between' }
                      : { justifyContent: 'flex-end' },
                  ]}
                >
                  {activeStep !== 0 && (
                    <Button
                      startIcon={<ChevronLeftRoundedIcon />}
                      onClick={handleBack}
                      variant="text"
                      sx={{ display: { xs: 'none', sm: 'flex' } }}
                      disabled={isSnackbarActive}
                    >
                      Previous
                    </Button>
                  )}
                  {activeStep !== 0 && (
                    <Button
                      startIcon={<ChevronLeftRoundedIcon />}
                      onClick={handleBack}
                      variant="outlined"
                      fullWidth
                      sx={{ display: { xs: 'flex', sm: 'none' } }}
                    >
                      Previous
                    </Button>
                  )}
                  <Button
                    variant="contained"
                    endIcon={<ChevronRightRoundedIcon />}
                    onClick={handleNext}
                    sx={{ width: { xs: '100%', sm: 'fit-content', fontFamily: 'math' } }}
                  >
                    {activeStep === steps.length - 2 ? 'Place order' : 'Next'}
                  </Button>
                </Box>
                <Snackbar
                  open={openSnackbar}
                  onClose={(event, reason) => {
                    // Only close when the reason is not "timeout"
                    if (reason !== 'timeout') {
                      return; // Do nothing if the close is due to a user action
                    }
                    setOpenSnackbar(false);
                    setIsSnackbarActive(false);
                  }}
                  message="Payment in progress..."
                  autoHideDuration={8000} // Automatically close after 8 seconds
                />
                <Overlay open={isSnackbarActive} />
                </>
                
              )}
            </Box>
          </Grid>
        </Grid>
      </ThemeProvider>
      </PaymentProvider>
    </TemplateFrame>
  );
}
